import React, { useState, useEffect, useRef } from 'react';
import { 
  PaperAirplaneIcon, 
  UserCircleIcon, 
  CpuChipIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  ChartBarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system' | 'error';
  content: string;
  agent?: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface SystemStats {
  total_tools: number;
  tool_categories: {
    github: number;
    jira: number;
    confluence: number;
    figma: number;
    basic: number;
  };
  builtin_agents: number;
  specialists: Array<{
    name: string;
    description: string;
    specialties: string[];
    tools_count: number;
  }>;
}

const Chat: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('Maestro');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mock system stats
  const mockSystemStats: SystemStats = {
    total_tools: 15,
    tool_categories: {
      github: 4,
      jira: 4,
      confluence: 3,
      figma: 2,
      basic: 2
    },
    builtin_agents: 2,
    specialists: [
      {
        name: 'SDLC Workflow Specialist',
        description: 'Expert in software development lifecycle automation',
        specialties: ['Code Review', 'CI/CD', 'Testing', 'Deployment'],
        tools_count: 8
      },
      {
        name: 'Project Management Specialist',
        description: 'Specialized in project coordination and team management',
        specialties: ['Sprint Planning', 'Issue Tracking', 'Team Coordination'],
        tools_count: 7
      }
    ]
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Check authentication
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Only load if authenticated
    if (isAuthenticated) {
      // Set mock system stats
      setSystemStats(mockSystemStats);
      
      // Add welcome message
      setMessages([{
        id: 'welcome',
        type: 'system',
        content: `🎭 Welcome to Orchestra's Chat Interface!

⚠️ **Note**: This is a frontend-only demo. The chat functionality has been disabled since we removed the backend services.

To enable chat functionality, you would need to:
• Set up a backend server with chat endpoints
• Implement WebSocket or Server-Sent Events for real-time communication
• Connect to AI services like OpenAI

For now, you can explore the other features:
• **Agents** - Create and manage AI agents
• **Workflows** - Design automation workflows  
• **Integrations** - Connect external tools
• **Monitoring** - View system metrics

Click on the sidebar to explore these features!`,
        agent: 'System',
        timestamp: new Date()
      }]);
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate a response
    setTimeout(() => {
      const responses = [
        "I'm currently in demo mode. To enable full chat functionality, please connect a backend service.",
        "This is a frontend-only demo. The chat interface would normally connect to AI agents for real conversations.",
        "In a full implementation, I would process your request and coordinate with specialized agents to help you.",
        "The chat functionality is disabled in this demo version. Please explore the other features in the sidebar!"
      ];
      
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      
      setMessages(prev => [...prev, {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: randomResponse,
        agent: 'Demo Assistant',
        timestamp: new Date()
      }]);
      
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getMessageIcon = (message: ChatMessage) => {
    switch (message.type) {
      case 'user':
        return <UserCircleIcon className="h-6 w-6 text-blue-500" />;
      case 'assistant':
        return <CpuChipIcon className="h-6 w-6 text-green-500" />;
      case 'system':
        return <InformationCircleIcon className="h-6 w-6 text-gray-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <CpuChipIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getMessageBgColor = (message: ChatMessage) => {
    switch (message.type) {
      case 'user':
        return 'bg-blue-50 dark:bg-blue-900/20';
      case 'assistant':
        return 'bg-green-50 dark:bg-green-900/20';
      case 'system':
        return 'bg-gray-50 dark:bg-gray-800/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20';
      default:
        return 'bg-gray-50 dark:bg-gray-800/50';
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CpuChipIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Chat Interface
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Demo Mode - Backend Disabled
              </p>
            </div>
          </div>
          
          {/* System Stats */}
          {systemStats && (
            <div className="hidden md:flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                <WrenchScrewdriverIcon className="h-4 w-4" />
                <span>{systemStats.total_tools} tools</span>
              </div>
              <div className="flex items-center space-x-1">
                <CpuChipIcon className="h-4 w-4" />
                <span>{systemStats.builtin_agents} specialists</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start space-x-3 p-4 rounded-lg ${getMessageBgColor(message)}`}
          >
            <div className="flex-shrink-0">
              {getMessageIcon(message)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {message.type === 'user' ? user?.full_name || 'You' : message.agent || 'Assistant'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {message.timestamp.toLocaleTimeString()}
                </span>
              </div>
              <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {message.content}
                {message.isStreaming && (
                  <span className="inline-block w-2 h-4 bg-gray-400 ml-1 animate-pulse" />
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <div className="flex-shrink-0">
              <CpuChipIcon className="h-6 w-6 text-gray-500 animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-500">Demo Assistant</span>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message... (Demo mode - limited functionality)"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                disabled={isLoading}
              />
            </div>
          </div>
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isLoading ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Demo Notice */}
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            ⚠️ Demo Mode: Chat functionality is limited without backend services
          </p>
        </div>
      </div>
    </div>
  );
};

export default Chat; 